import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCookies } from '../contexts/CookieContext';
import PrivacyModal from './PrivacyModal';

const { FiCookie, FiSettings, FiCheck, FiX, FiInfo } = FiIcons;

const CookieConsent = () => {
  const {
    showCookieBanner,
    cookiePreferences,
    acceptAllCookies,
    acceptNecessaryOnly,
    updatePreferences,
    setShowCookieBanner
  } = useCookies();

  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(cookiePreferences);

  const handleSavePreferences = () => {
    updatePreferences(tempPreferences);
    setShowSettings(false);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Nødvendige cookies',
      description: 'Disse er påkrevd for at nettsiden skal fungere korrekt. De kan ikke deaktiveres.',
      required: true,
      examples: ['Innloggingsstatus', 'Sikkerhetstokens', 'Språkinnstillinger']
    },
    {
      id: 'functional',
      name: 'Funksjonelle cookies',
      description: 'Disse forbedrer funksjonaliteten og personaliseringen av nettsiden.',
      required: false,
      examples: ['Brukerpreferanser', 'Skjemadata', 'Chathistorikk']
    },
    {
      id: 'analytics',
      name: 'Analyse cookies',
      description: 'Disse hjelper oss å forstå hvordan nettsiden brukes og forbedre den.',
      required: false,
      examples: ['Google Analytics', 'Bruksstatistikk', 'Ytelsesmålinger']
    },
    {
      id: 'marketing',
      name: 'Markedsføring cookies',
      description: 'Disse brukes til å vise relevante annonser og markedsføringsinnhold.',
      required: false,
      examples: ['Annonsetargeting', 'Sosiale medier', 'Retargeting']
    }
  ];

  if (!showCookieBanner) return null;

  return (
    <>
      <AnimatePresence>
        {showCookieBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
          >
            <div className="max-w-7xl mx-auto px-4 py-6">
              {!showSettings ? (
                // Main cookie banner
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCookie} className="text-blue-600 text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Vi bruker informasjonskapsler
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Vi bruker nødvendige og valgfrie cookies for å forbedre din opplevelse på vår nettside. 
                        Nødvendige cookies er påkrevd for grunnleggende funksjonalitet, mens valgfrie cookies 
                        hjelper oss med å analysere bruk og forbedre tjenesten.{' '}
                        <button
                          onClick={() => setShowPrivacyModal(true)}
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Les mer om personvern
                        </button>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 lg:ml-4">
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <SafeIcon icon={FiSettings} />
                      Tilpass valg
                    </button>
                    <button
                      onClick={acceptNecessaryOnly}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      Kun nødvendige
                    </button>
                    <button
                      onClick={acceptAllCookies}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Godta alle
                    </button>
                  </div>
                </div>
              ) : (
                // Cookie settings panel
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Innstillinger for informasjonskapsler
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="text-gray-500" />
                    </button>
                  </div>

                  <div className="grid gap-4">
                    {cookieTypes.map((type) => (
                      <div key={type.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium text-gray-900">{type.name}</h4>
                            {type.required && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                Påkrevd
                              </span>
                            )}
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={tempPreferences[type.id]}
                              onChange={(e) => setTempPreferences({
                                ...tempPreferences,
                                [type.id]: e.target.checked
                              })}
                              disabled={type.required}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                        <div className="text-xs text-gray-500">
                          <strong>Eksempler:</strong> {type.examples.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowSettings(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <SafeIcon icon={FiCheck} />
                      Lagre innstillinger
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <PrivacyModal 
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </>
  );
};

export default CookieConsent;